import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Search, Plus, Download, Mail, Shield, Car } from 'lucide-react';
import { formatDate, formatCurrency, delay } from '../utils/helpers';
import { generateCertificadoPDF } from '../services/pdfService';
import { generateGarantia, generateVehiculo } from '../data/mockData';
import toast from 'react-hot-toast';

const GarantiasListPage = () => {
  const { mockData, user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterNivel, setFilterNivel] = useState('');

  const garantias = React.useMemo(() => {
    let filtered = mockData?.garantias || [];

    if (user?.role === 'vendedor') {
      filtered = filtered.filter(g => g.vendedor === user.email);
    }

    if (searchTerm) {
      filtered = filtered.filter(g =>
        g.vehiculo.matricula.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.propietario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.numeroGarantia.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterEstado) {
      filtered = filtered.filter(g => g.estado === filterEstado);
    }

    if (filterNivel) {
      filtered = filtered.filter(g => g.nivel === parseInt(filterNivel));
    }

    return filtered;
  }, [mockData, searchTerm, filterEstado, filterNivel, user]);

  const columns = [
    {
      header: 'Nº Garantía',
      accessor: 'numeroGarantia',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold">{row.numeroGarantia}</span>
      )
    },
    {
      header: 'Vehículo',
      cell: (row) => (
        <div>
          <p className="font-medium">{row.vehiculo.matricula}</p>
          <p className="text-xs text-gray-500">{row.vehiculo.marca} {row.vehiculo.modelo}</p>
        </div>
      )
    },
    {
      header: 'Propietario',
      accessor: 'propietario.nombre'
    },
    {
      header: 'Nivel',
      cell: (row) => {
        const colors = ['default', 'primary', 'warning', 'success'];
        return <Badge variant={colors[row.nivel - 1]}>{row.nivelNombre}</Badge>;
      }
    },
    {
      header: 'Fecha Alta',
      cell: (row) => formatDate(row.fechaAlta)
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.estado === 'activa' ? 'success' : 'default'}>
          {row.estado.toUpperCase()}
        </Badge>
      )
    },
    {
      header: 'Precio',
      cell: (row) => <span className="font-semibold">{formatCurrency(row.precio)}</span>
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Garantías</h1>
          <p className="text-gray-600 mt-1">Gestión de garantías de vehículos</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => navigate('/garantias/nueva')}
        >
          Nueva Garantía
        </Button>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Input
            placeholder="Buscar por matrícula, cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
          <Select
            placeholder="Todos los estados"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            options={[
              { value: 'activa', label: 'Activa' },
              { value: 'finalizada', label: 'Finalizada' }
            ]}
          />
          <Select
            placeholder="Todos los niveles"
            value={filterNivel}
            onChange={(e) => setFilterNivel(e.target.value)}
            options={[
              { value: '1', label: 'Básica' },
              { value: '2', label: 'Estándar' },
              { value: '3', label: 'Premium' },
              { value: '4', label: 'Total' }
            ]}
          />
          <Button variant="outline" fullWidth>
            Exportar CSV
          </Button>
        </div>

        <Table
          columns={columns}
          data={garantias}
          onRowClick={(row) => navigate(`/garantias/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const NuevaGarantiaPage = () => {
  const { mockData, updateMockData, addAuditLog, user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchingVehicle, setSearchingVehicle] = useState(false);

  const [matricula, setMatricula] = useState('');
  const [vehiculo, setVehiculo] = useState(null);
  const [propietario, setPropietario] = useState({
    nombre: '',
    dni: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    codigoPostal: ''
  });
  const [nivel, setNivel] = useState('2');

  const buscarVehiculo = async () => {
    setSearchingVehicle(true);
    await delay(1500);
    
    const vehiculoMock = generateVehiculo();
    vehiculoMock.matricula = matricula.toUpperCase();
    setVehiculo(vehiculoMock);
    setSearchingVehicle(false);
    toast.success('Vehículo encontrado');
    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    await delay(1000);

    const nuevaGarantia = {
      ...generateGarantia(),
      vehiculo,
      propietario,
      nivel: parseInt(nivel),
      nivelNombre: ['Básica', 'Estándar', 'Premium', 'Total'][parseInt(nivel) - 1],
      vendedor: user.email,
      fechaAlta: new Date().toISOString()
    };

    const updatedGarantias = [nuevaGarantia, ...mockData.garantias];
    updateMockData({ garantias: updatedGarantias });
    addAuditLog('crear_garantia', 'garantia', nuevaGarantia.id, `Garantía ${nuevaGarantia.numeroGarantia} creada`);
    addNotification('success', 'Garantía creada', `Se ha creado la garantía ${nuevaGarantia.numeroGarantia}`);

    toast.success('Garantía creada exitosamente');
    setLoading(false);
    
    generateCertificadoPDF(nuevaGarantia);
    
    setTimeout(() => {
      navigate('/garantias');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Nueva Garantía</h1>
        <p className="text-gray-600 mt-1">Registro rápido de garantía de vehículo</p>
      </div>

      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {step === 1 && (
        <Card title="Paso 1: Buscar Vehículo">
          <div className="max-w-md mx-auto space-y-4">
            <Input
              label="Matrícula"
              value={matricula}
              onChange={(e) => setMatricula(e.target.value)}
              placeholder="1234ABC"
              icon={Car}
              required
            />
            <Button
              variant="primary"
              fullWidth
              onClick={buscarVehiculo}
              loading={searchingVehicle}
              disabled={!matricula}
            >
              Buscar Vehículo
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && vehiculo && (
        <Card title="Paso 2: Datos del Propietario">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre Completo"
              value={propietario.nombre}
              onChange={(e) => setPropietario({ ...propietario, nombre: e.target.value })}
              required
            />
            <Input
              label="DNI"
              value={propietario.dni}
              onChange={(e) => setPropietario({ ...propietario, dni: e.target.value })}
              required
            />
            <Input
              label="Teléfono"
              value={propietario.telefono}
              onChange={(e) => setPropietario({ ...propietario, telefono: e.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              value={propietario.email}
              onChange={(e) => setPropietario({ ...propietario, email: e.target.value })}
              required
            />
            <Input
              label="Dirección"
              value={propietario.direccion}
              onChange={(e) => setPropietario({ ...propietario, direccion: e.target.value })}
              className="md:col-span-2"
            />
            <Input
              label="Ciudad"
              value={propietario.ciudad}
              onChange={(e) => setPropietario({ ...propietario, ciudad: e.target.value })}
            />
            <Input
              label="Código Postal"
              value={propietario.codigoPostal}
              onChange={(e) => setPropietario({ ...propietario, codigoPostal: e.target.value })}
            />
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)}>
              Atrás
            </Button>
            <Button
              variant="primary"
              onClick={() => setStep(3)}
              disabled={!propietario.nombre || !propietario.dni}
            >
              Siguiente
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card title="Paso 3: Nivel de Cobertura">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { nivel: '1', nombre: 'Básica', precio: 299, color: 'gray' },
              { nivel: '2', nombre: 'Estándar', precio: 499, color: 'blue' },
              { nivel: '3', nombre: 'Premium', precio: 699, color: 'yellow' },
              { nivel: '4', nombre: 'Total', precio: 899, color: 'green' }
            ].map((option) => (
              <button
                key={option.nivel}
                onClick={() => setNivel(option.nivel)}
                className={`p-6 border-2 rounded-xl transition-all ${
                  nivel === option.nivel
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Shield className={`w-8 h-8 mb-3 ${nivel === option.nivel ? 'text-primary-600' : 'text-gray-400'}`} />
                <h3 className="font-bold text-lg mb-2">{option.nombre}</h3>
                <p className="text-2xl font-bold text-primary-600">{formatCurrency(option.precio)}</p>
              </button>
            ))}
          </div>

          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h4 className="font-semibold mb-4">Resumen</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Vehículo:</span>
                <span className="font-semibold">{vehiculo.marca} {vehiculo.modelo}</span>
              </div>
              <div className="flex justify-between">
                <span>Matrícula:</span>
                <span className="font-semibold">{vehiculo.matricula}</span>
              </div>
              <div className="flex justify-between">
                <span>Propietario:</span>
                <span className="font-semibold">{propietario.nombre}</span>
              </div>
              <div className="flex justify-between">
                <span>Nivel:</span>
                <span className="font-semibold">{['Básica', 'Estándar', 'Premium', 'Total'][parseInt(nivel) - 1]}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              Atrás
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              loading={loading}
              icon={Download}
            >
              Generar Certificado
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

const GarantiaDetallePage = () => {
  const { id } = useParams();
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [garantia, setGarantia] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const loadGarantia = async () => {
      await delay(300);
      const found = mockData?.garantias?.find(g => g.id === id);
      setGarantia(found);
      setLoading(false);
    };
    loadGarantia();
  }, [id, mockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!garantia) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Garantía no encontrada</h2>
          <p className="text-gray-600 mb-6">No se pudo encontrar la garantía solicitada</p>
          <Button onClick={() => navigate('/garantias')}>
            Volver al listado
          </Button>
        </div>
      </div>
    );
  }

  const diasRestantes = Math.ceil((new Date(garantia.fechaVencimiento) - new Date()) / (1000 * 60 * 60 * 24));
  const estaProximoVencer = diasRestantes <= 30 && diasRestantes > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Detalles de Garantía</h1>
          <p className="text-gray-600 mt-1">Información completa de la garantía</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/garantias')}>
          Volver
        </Button>
      </div>

      {estaProximoVencer && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <p className="text-yellow-800 font-medium">Garantía próxima a vencer</p>
              <p className="text-yellow-700 text-sm">Quedan {diasRestantes} días</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Información de Garantía">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Número de Garantía</p>
                <p className="font-semibold text-gray-900">{garantia.numeroGarantia}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Estado</p>
                <Badge variant={garantia.estado === 'activa' ? 'success' : 'default'}>
                  {garantia.estado.toUpperCase()}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Nivel de Cobertura</p>
                <p className="font-semibold text-gray-900">{garantia.nivelNombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Precio</p>
                <p className="font-semibold text-gray-900">{formatCurrency(garantia.precio)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fecha de Alta</p>
                <p className="font-semibold text-gray-900">{formatDate(garantia.fechaAlta)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fecha de Vencimiento</p>
                <p className="font-semibold text-gray-900">{formatDate(garantia.fechaVencimiento)}</p>
              </div>
            </div>
          </Card>

          <Card title="Información del Vehículo">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Matrícula</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.matricula}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bastidor</p>
                <p className="font-semibold text-gray-900 font-mono text-xs">{garantia.vehiculo.bastidor}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Marca y Modelo</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.marca} {garantia.vehiculo.modelo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Versión</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.version}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Año</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.year}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Combustible</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.combustible}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Potencia</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.potencia} CV</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Kilometraje</p>
                <p className="font-semibold text-gray-900">{garantia.vehiculo.kilometros?.toLocaleString()} km</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Propietario">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Nombre</p>
                <p className="font-semibold text-gray-900">{garantia.propietario.nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">DNI</p>
                <p className="font-semibold text-gray-900">{garantia.propietario.dni}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Teléfono</p>
                <p className="font-semibold text-gray-900">{garantia.propietario.telefono}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold text-gray-900 text-sm break-all">{garantia.propietario.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dirección</p>
                <p className="font-semibold text-gray-900">{garantia.propietario.direccion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ciudad</p>
                <p className="font-semibold text-gray-900">{garantia.propietario.ciudad} ({garantia.propietario.codigoPostal})</p>
              </div>
            </div>
          </Card>

          <Card title="Acciones">
            <div className="space-y-2">
              <Button
                variant="primary"
                className="w-full"
                icon={Download}
                onClick={() => generateCertificadoPDF(garantia)}
              >
                Descargar Certificado
              </Button>
              <Button
                variant="outline"
                className="w-full"
                icon={Mail}
                onClick={() => toast.success('Email enviado')}
              >
                Enviar por Email
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const GarantiasPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<GarantiasListPage />} />
        <Route path="nueva" element={<NuevaGarantiaPage />} />
        <Route path=":id" element={<GarantiaDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default GarantiasPage;
